import {ChangeDetectionStrategy, Component, inject, input, output, signal} from '@angular/core';
import {FormsModule} from '@angular/forms';
import {MessageErrorDetails} from '../entities/message-error-details.model';
import {AttachmentUploadViewModel} from '../view-models/attachment-upload-view.model';
import {AttachmentValidationService} from '../services/attachment-validation.service';
import {Utils} from '@shared/utilities/utils';
import {DropdownComponent} from '@components/ui/dropdown/dropdown.component';
import {DropdownTriggerForDirective} from '@components/ui/dropdown/directives/dropdown-trigger-for.directive';
import {MessageAttachmentMenuComponent} from '../message-attachment-menu/message-attachment-menu.component';
import {MessageSendEvent} from '../entities/message-send-event.model';

@Component({
    selector: 'app-message-input-bar',
    templateUrl: './message-input-bar.component.html',
    styleUrls: ['./message-input-bar.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [FormsModule, DropdownComponent, DropdownTriggerForDirective, MessageAttachmentMenuComponent]
})
export class MessageInputBarComponent {

  readonly isLoading = input(false);
  readonly attachments = signal<AttachmentUploadViewModel[]>([]);
  messageValue = '';
  readonly messageCanBeSent = signal(false);
  readonly hint = signal('Enter text');
  readonly numberOfAttachments = signal(0);

  readonly messageSent = output<MessageSendEvent>();
  readonly messagesRefreshed = output<string>();
  readonly attachmentError = output<MessageErrorDetails>();

  private readonly attachmentValidationService = inject(AttachmentValidationService);

  public updateSendEnabled(): void {
    this.numberOfAttachments.set(this.attachments().length);
    const hasMessageText = !!(this.messageValue && this.messageValue.trim().length > 0);
    this.messageCanBeSent.set(!this.isLoading() && (hasMessageText || this.attachments().length > 0));
  }

  public messageSend(): void {
    this.updateSendEnabled();
    if (this.messageCanBeSent()) {
      const messageText = this.messageValue.substring(0, 2000);
      const sendMessage: MessageSendEvent = {
        content: messageText,
        uploadAttachments: this.attachments()
      };
      this.messageSent.emit(sendMessage);
    }
  }

  public clearMessage(): void {
    this.messageValue = '';
    this.attachments.set([]);
    this.updateSendEnabled();
  }

  public addFiles(files: File[]): void {
    if (this.isLoading()) {
      return;
    }

    const attachmentListError: MessageErrorDetails = this.attachmentValidationService.validateNewAttachmentsList(this.attachments(), files);

    if (attachmentListError) {
      this.attachmentError.emit(attachmentListError);
      return;
    }

    for (const attachment of files) {
      const attachmentError: MessageErrorDetails = this.attachmentValidationService.validateAttachment(attachment);

      if (attachmentError) {
        this.attachmentError.emit(attachmentError);
        return;
      }
    }

    const attachmentViewModels = files.map(file => this.convertFileToAttachmentUploadViewModel(file));
    this.attachments.update(list => [...list, ...attachmentViewModels]);
    this.updateSendEnabled();
  }

  private convertFileToAttachmentUploadViewModel(file: File): AttachmentUploadViewModel {
    const extension = file.name.split('.').pop().toLowerCase();
    const iconUrl = Utils.getFileIconSource(extension);
    return {
      filename: file.name,
      fileSize: file.size,
      iconUrl: iconUrl,
      file: file
    };
  }

  removeAttachment(attachment: AttachmentUploadViewModel): void {
    if (this.isLoading()) {
      return;
    }

    this.attachments.update(list => list.filter(a => a !== attachment));
    this.updateSendEnabled();
  }
}
